import { useEffect, useState } from "react";

import Ship from "@/assets/icons/ship.svg?react";
import type {
  HomeObraEmAndamento,
  HomeObraFinanceiro,
  HomeObraGmud,
  HomeObraProgressoItem,
  HomeObrasEmAndamento,
} from "@/modules/home";
import {
  formatContagem,
  formatDataCurta,
  formatDuracao,
  formatFracao,
  formatPercentual,
  formatQuantidade,
  formatValorCompacto,
} from "../../home.format";
import { HomeCard } from "../home-card";
import { HomeChart } from "../home-chart";
import styles from "./obras-view.module.scss";

/** Tarja de identificação da Obra: número, período, duração e embarcação. */
function ObraChips({ obra }: { obra: HomeObraEmAndamento }) {
  const { inicio, termino, duracaoDias } = obra.periodo;

  return (
    <div className={styles.chips}>
      <span className={styles.chip}>ID {obra.id}</span>

      <span className={styles.chip}>
        <i className={`pi pi-calendar ${styles.iconCalendario}`} aria-hidden />

        <span className={styles.periodo}>
          <span>{formatDataCurta(inicio)}</span>
          <span aria-hidden>→</span>
          <span>{formatDataCurta(termino)}</span>
        </span>

        <span className={styles.duracao}>
          <i className={`pi pi-clock ${styles.iconRelogio}`} aria-hidden />
          <span>{formatDuracao(duracaoDias)}</span>
        </span>
      </span>

      {/* Terceira tarja do protótipo, só com o ícone: identifica que a Obra é de
          embarcação. A modalidade ainda não faz parte do contrato de dados, por
          isso o ícone é fixo até que as variações sejam definidas. */}
      <span className={`${styles.chip} ${styles.chipIcone}`} data-testid="home-obra-embarcacao">
        <Ship className={styles.iconNavio} role="img" aria-label="Obra de embarcação" />
      </span>
    </div>
  );
}

/**
 * Cartão Financeiro da Obra: valor total, curva de desembolso com o marcador
 * "Hoje" e a participação de cada fase (AC5/AC6).
 */
function FinanceiroCard({ financeiro }: { financeiro: HomeObraFinanceiro }) {
  return (
    <HomeCard title="Financeiro" className={styles.financeiro} testId="home-card-financeiro">
      <div className={styles.financeiroHeadline}>
        <strong className={styles.financeiroValor} data-testid="home-financeiro-valor">
          {formatValorCompacto(financeiro.valorTotal)}
        </strong>

        <div className={styles.financeiroFel}>
          <span className={styles.felTag}>{financeiro.fel}</span>
          <span className={styles.felLegenda}>{financeiro.legenda}</span>
        </div>
      </div>

      <HomeChart
        points={financeiro.serie}
        ariaLabel="Curva de desembolso da Obra, em milhões de reais"
        labelHeader="Período"
        valueHeader="Desembolso"
        formatValue={formatValorCompacto}
        borderWidth={0}
        markerIndex={financeiro.indiceHoje}
        showGrid={false}
        className={styles.financeiroChart}
        testId="home-chart-financeiro"
      />

      <ul className={styles.fases}>
        {financeiro.fases.map((fase) => (
          <li key={fase.codigo} className={styles.fase} data-testid={`home-fase-${fase.sigla}`}>
            <span className={styles.faseSigla}>{fase.sigla}</span>
            <span className={styles.fasePercentual}>{formatPercentual(fase.percentual)}</span>
            <span className={styles.faseCodigo}>{fase.codigo}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/** Cartão Obra: avanço dos escopos da Obra em execução. */
function ObraProgressoCard({ progresso }: { progresso: HomeObraProgressoItem[] }) {
  return (
    <HomeCard title="Obra" className={styles.progresso} testId="home-card-obra">
      <ul className={styles.progressoList}>
        {progresso.map((item) => (
          <li key={item.label} className={styles.progressoItem}>
            <div className={styles.progressoNumbers}>
              <strong className={styles.progressoPercentual}>
                {formatPercentual(item.percentual)}
              </strong>
              <span className={styles.progressoFracao}>
                {formatFracao(item.realizado, item.total)}
              </span>
            </div>

            <span className={styles.progressoLabel}>{item.label}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

const GMUD_NATUREZAS = [
  { key: "adicao", label: "Adição", icon: "pi pi-file-plus" },
  { key: "exclusao", label: "Exclusão", icon: "pi pi-file-excel" },
  { key: "alteracao", label: "Alteração", icon: "pi pi-file-edit" },
  { key: "quebra", label: "Quebra", icon: "pi pi-clock" },
] as const;

/** Cartão GMUD: aprovações e contagem por natureza da mudança. */
function GmudCard({ gmud }: { gmud: HomeObraGmud }) {
  return (
    <HomeCard title="GMUD" className={styles.gmud} testId="home-card-gmud">
      <div className={styles.gmudMobileHeadline}>
        <h2 className={styles.gmudMobileTitle}>GMUD</h2>
        <div className={styles.gmudHeadline}>
          <strong className={styles.gmudAprovadas}>{formatQuantidade(gmud.aprovadas)}</strong>
          <span className={styles.gmudTotal}>/{formatQuantidade(gmud.total)}</span>
          <span className={styles.gmudLegenda}>Aprovadas</span>
        </div>
      </div>

      <ul className={styles.gmudGrid}>
        {GMUD_NATUREZAS.map((natureza) => (
          <li
            key={natureza.key}
            className={styles.gmudItem}
            data-testid={`home-gmud-${natureza.key}`}
          >
            <span className={styles.gmudItemValue}>
              <i className={`${natureza.icon} ${styles.gmudIcon}`} aria-hidden />
              {formatContagem(gmud[natureza.key])}
            </span>
            <span className={styles.gmudItemLabel}>{natureza.label}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/**
 * Visão Obras em Andamento: um destaque por Obra em execução, percorrido pelos
 * controles do rodapé, como no protótipo (AC2).
 */
export function ObrasView({ obrasEmAndamento }: { obrasEmAndamento: HomeObrasEmAndamento }) {
  const { obras } = obrasEmAndamento;
  const [index, setIndex] = useState(0);

  // Uma consulta nova pode trazer menos Obras do que a anterior; sem isto o
  // destaque ficaria em um índice que não existe mais.
  useEffect(() => {
    setIndex((current) => (current < obras.length ? current : 0));
  }, [obras.length]);

  const obra = obras[index];
  if (!obra) return null;

  const total = obras.length;
  const irPara = (proximo: number) => setIndex((proximo + total) % total);

  return (
    <div className={styles.container} data-testid="home-obras-em-andamento">
      <article className={styles.destaque} data-testid={`home-obra-${obra.id}`}>
        <header className={styles.destaqueHeader}>
          <ObraChips obra={obra} />
          <h2 className={styles.chamada}>{obra.chamada}</h2>
          <p className={styles.descricao}>{obra.descricao}</p>
        </header>

        <div className={styles.cards}>
          <FinanceiroCard financeiro={obra.financeiro} />
          <ObraProgressoCard progresso={obra.progresso} />
          <GmudCard gmud={obra.gmud} />
        </div>

        {/* No protótipo o acesso fica acima dos indicadores do carrossel, à
            esquerda, e as setas ficam sozinhas no canto direito. */}
        <footer className={styles.destaqueFooter}>
          <div className={styles.footerStart}>
            {/* As rotas de Obra ainda não existem (`field.routes.tsx` está
                vazio): o acesso aparece como no protótipo, porém desabilitado,
                para não oferecer um destino que a aplicação não tem. */}
            <button
              type="button"
              className={styles.acessar}
              disabled
              title="Disponível quando o módulo de Obras for liberado no Hub."
              data-testid="home-obra-acessar"
            >
              Acessar Obra
              <i className="pi pi-arrow-right" aria-hidden />
            </button>

            {total > 1 ? (
              <div className={styles.dots} role="tablist" aria-label="Obras em Andamento">
                {obras.map((item, itemIndex) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    className={[styles.dot, itemIndex === index ? styles.dotActive : ""]
                      .filter(Boolean)
                      .join(" ")}
                    aria-selected={itemIndex === index}
                    aria-label={`Obra ${item.id}`}
                    onClick={() => setIndex(itemIndex)}
                    data-testid={`home-obra-dot-${item.id}`}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {total > 1 ? (
            <div className={styles.arrows}>
              <button
                type="button"
                className={styles.arrow}
                aria-label="Obra anterior"
                onClick={() => irPara(index - 1)}
                data-testid="home-obra-anterior"
              >
                <i className="pi pi-chevron-left" aria-hidden />
              </button>

              <button
                type="button"
                className={styles.arrow}
                aria-label="Próxima Obra"
                onClick={() => irPara(index + 1)}
                data-testid="home-obra-proxima"
              >
                <i className="pi pi-chevron-right" aria-hidden />
              </button>
            </div>
          ) : null}
        </footer>
      </article>
    </div>
  );
}
